'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

const AUTH_STORAGE_VERSION = 2;
const AUTH_VERSION_KEY = 'petmarket_auth_version';

const getStoredUser = () => {
  try {
    const version = localStorage.getItem(AUTH_VERSION_KEY);
    if (Number(version) !== AUTH_STORAGE_VERSION) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.setItem(AUTH_VERSION_KEY, String(AUTH_STORAGE_VERSION));
      return null;
    }
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      return JSON.parse(savedUser);
    }
  } catch {
    return null;
  }
  return null;
};

export const AuthProvider = ({ children }) => {
  // Initialize as null (matches server), then hydrate from localStorage after mount
  const [user, setUser] = useState(null);
  const loading = false;

  useEffect(() => {
    const stored = getStoredUser();
    if (stored) setUser(stored);
  }, []);

  const googleLogin = async (credential) => {
    const { data } = await api.post('/auth/google', { credential });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem(AUTH_VERSION_KEY, String(AUTH_STORAGE_VERSION));
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(AUTH_VERSION_KEY);
    setUser(null);
  };

  const updateUser = (updatedFields) => {
    setUser((prev) => {
      const updated = { ...prev, ...updatedFields };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <AuthContext.Provider value={{ user, googleLogin, logout, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
